/**
 * Thin wrapper around `state/store.ts` that also enqueues the
 * corresponding outbox row for each mutation. `store.ts` itself stays
 * pure/localStorage-only (no knowledge of sync) — call sites that need
 * to sync a change go through here instead of calling `store.ts`
 * directly, so "mutate + enqueue" can never drift apart.
 */
import type { Grade, ItemKind } from "@repo/hindi-shared";

import { enqueue } from "./outbox";
import type { AppState } from "./store";
import { applyGrade, completeLesson } from "./store";
import { SETTINGS_TIMESTAMP_KEY } from "./useSync";

/** Grade a review item and enqueue the resulting row for sync. */
export function applyGradeSynced(
  s: AppState,
  itemId: string,
  kind: ItemKind | "word" | "sentence",
  grade: Grade,
  now: number,
  rand: () => number,
): AppState {
  const next = applyGrade(s, itemId, kind, grade, now, rand);
  const review = next.reviews[itemId];
  if (review !== undefined) {
    enqueue({
      reviews: [
        {
          itemId,
          kind,
          ease: review.ease,
          intervalDays: review.intervalDays,
          reps: review.reps,
          lapses: review.lapses,
          dueAt: review.dueAt,
          updatedAt: next.updatedAt[itemId] ?? now,
        },
      ],
    });
  }
  return next;
}

/** Mark a lesson complete and enqueue the resulting row for sync. */
export function completeLessonSynced(
  s: AppState,
  lessonId: string,
  now: number,
): AppState {
  const next = completeLesson(s, lessonId, now);
  if (!s.completedLessonIds.includes(lessonId)) {
    enqueue({ lessons: [{ lessonId, completedAt: now }] });
  }
  return next;
}

/** Record a finished session and enqueue it for sync. */
export function recordSessionSynced(session: {
  id: string;
  dateKey: string;
  startedAt: number;
  durationSec: number;
  accuracy: number;
}): void {
  enqueue({ sessions: [session] });
}

/**
 * Persist settings and enqueue the settings row for sync. Stamps
 * `updatedAt[SETTINGS_TIMESTAMP_KEY]` so `mergeBootstrap` knows the
 * learner has edited settings on this device (see `useSync.ts`).
 */
export function saveSettingsSynced(
  s: AppState,
  settings: AppState["settings"],
  now: number,
): AppState {
  const next: AppState = {
    ...s,
    settings,
    updatedAt: { ...s.updatedAt, [SETTINGS_TIMESTAMP_KEY]: now },
  };
  enqueue({ settings: { json: JSON.stringify(settings), updatedAt: now } });
  return next;
}
